import { createFileRoute } from '@tanstack/react-router'
import { useQuery as useConvexQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useAuth } from '@clerk/clerk-react'
import { useMemo, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  flexRender,
} from '@tanstack/react-table'
import Fuse from 'fuse.js'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import type { Doc, Id } from 'convex/_generated/dataModel'

dayjs.extend(relativeTime)

export const Route = createFileRoute('/preview/schedule/')({
  component: PreviewVocabularyDashboard,
})

function PreviewVocabularyDashboard() {
  const { isSignedIn } = useAuth()
  const currentUser = useCurrentUser()

  // Show loading state while checking auth status
  if (isSignedIn && currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  // If signed in, use real user data
  if (isSignedIn && currentUser) {
    return <VocabularyDashboardContent userId={currentUser._id} isPreview={false} />
  }

  // If not signed in, use preview user data
  return <VocabularyDashboardContent userId={null} isPreview={true} />
}

function VocabularyDashboardContent({
  userId,
  isPreview
}: {
  userId: Id<'users'> | null
  isPreview: boolean
}) {
  // Query vocabulary items - use preview queries if preview mode, otherwise use regular queries
  const vocabularyItems = useConvexQuery(
    isPreview ? api.preview.getPreviewVocabulary : api.vocabulary.getAllActive,
    (isPreview ? { includeArchived: false } : (userId ? { userId, includeArchived: false } : 'skip')) as any
  )

  // Query due items count
  const dueItems = useConvexQuery(
    isPreview ? api.preview.getPreviewDueVocabulary : api.vocabulary.getDueItems,
    (isPreview ? { limit: 100 } : (userId ? { userId, limit: 100 } : 'skip')) as any
  )

  // Query mastered items count
  const masteredItems = useConvexQuery(
    isPreview ? api.preview.getPreviewMasteredVocabulary : api.vocabulary.getMastered,
    (isPreview ? {} : (userId ? { userId } : 'skip')) as any
  )

  const isLoading = vocabularyItems === undefined
  const items = vocabularyItems ?? []
  const dueCount = dueItems?.length ?? 0
  const masteredCount = masteredItems?.length ?? 0

  return (
    <div className="min-h-screen bg-black text-white py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Preview Banner */}
        {isPreview && (
          <div className="mb-4 bg-terminal-green/20 border-2 border-terminal-green p-3 text-center">
            <p className="text-terminal-green font-mono text-sm font-bold">
              [PREVIEW MODE] • Sign up to save your vocabulary progress
            </p>
          </div>
        )}

        {/* Header */}
        <div className="mb-8 terminal-border p-6">
          <h1 className="text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
            [VOCABULARY DASHBOARD]
          </h1>
          <p className="text-trail-tan font-mono">
            Manage your vocabulary learning with spaced repetition
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Items"
            value={items.length}
            icon="📚"
            color="blue"
          />
          <StatCard
            title="Due for Review"
            value={dueCount}
            icon="🔔"
            color="yellow"
          />
          <StatCard
            title="Mastered"
            value={masteredCount}
            icon="🎓"
            color="green"
          />
          <StatCard
            title="Accuracy"
            value={calculateAccuracy(items)}
            icon="📊"
            color="purple"
            isPercentage
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-6 flex-wrap">
          <a
            href={isPreview ? "/preview/schedule" : "/app/schedule/review"}
            className="px-6 py-3 bg-terminal-green hover:bg-terminal-green/80 text-black font-bold font-mono rounded-lg transition-colors disabled:bg-gray-400 border-2 border-terminal-green"
          >
            START REVIEW SESSION ({dueCount} due)
          </a>
          <a
            href={isPreview ? "/preview/schedule/calendar" : "/app/schedule/calendar"}
            className="px-6 py-3 bg-black hover:bg-gray-900 text-trail-tan font-bold font-mono rounded-lg transition-colors border-2 border-trail-tan"
          >
            CALENDAR VIEW
          </a>
          <a
            href={isPreview ? "/preview/schedule/analytics" : "/app/schedule/analytics"}
            className="px-6 py-3 bg-black hover:bg-gray-900 text-trail-tan font-bold font-mono rounded-lg transition-colors border-2 border-trail-tan"
          >
            ANALYTICS
          </a>
        </div>

        {/* Vocabulary Table */}
        <div className="terminal-border bg-black">
          {isLoading ? (
            <div className="p-6">
              <p className="text-trail-tan font-mono">Loading vocabulary...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="p-6">
              <p className="text-trail-tan font-mono">
                No vocabulary items yet. Generate some songs with lyrics on the{' '}
                <a href={isPreview ? "/preview/songs" : "/app/songs"} className="text-sky-blue hover:text-terminal-green underline">
                  Songs
                </a>{' '}
                page to extract vocabulary!
              </p>
            </div>
          ) : (
            <VocabularyTable items={items} />
          )}
        </div>
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  icon,
  color,
  isPercentage = false,
}: {
  title: string
  value: number
  icon: string
  color: 'blue' | 'yellow' | 'green' | 'purple'
  isPercentage?: boolean
}) {
  const colorClasses = {
    blue: 'bg-blue-900/30 border-blue-500 text-blue-300',
    yellow: 'bg-yellow-900/30 border-yellow-500 text-yellow-300',
    green: 'bg-green-900/30 border-green-500 text-green-300',
    purple: 'bg-purple-900/30 border-purple-500 text-purple-300',
  }

  const textColorClasses = {
    blue: 'text-blue-300',
    yellow: 'text-yellow-300',
    green: 'text-green-300',
    purple: 'text-purple-300',
  }

  return (
    <div className={`terminal-border p-6 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        <span className={`text-3xl font-bold font-mono ${textColorClasses[color]}`}>
          {isPercentage ? `${value}%` : value}
        </span>
      </div>
      <p className="text-sm font-mono font-medium">{title}</p>
    </div>
  )
}

function VocabularyTable({ items }: { items: Doc<'vocabularyItems'>[] }) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'dueDate', desc: false }, // Default sort by due date ascending (soonest first)
  ])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  })

  // Fuzzy search with Fuse.js
  const fuzzyFilteredItems = useMemo(() => {
    if (!globalFilter) return items

    const fuse = new Fuse(items, {
      keys: ['word', 'translation', 'context', 'definition', 'notes'],
      threshold: 0.3, // 0 = exact match, 1 = match anything
      ignoreLocation: true,
    })

    const results = fuse.search(globalFilter)
    return results.map((result) => result.item)
  }, [items, globalFilter])

  // Column definitions
  const columns = useMemo<ColumnDef<Doc<'vocabularyItems'>>[]>(
    () => [
      {
        accessorKey: 'word',
        header: 'Word',
        cell: (info) => (
          <div>
            <p className="font-semibold text-white font-mono">{info.getValue() as string}</p>
            {info.row.original.translation && (
              <p className="text-sm text-trail-tan font-mono">{info.row.original.translation}</p>
            )}
            {info.row.original.partOfSpeech && (
              <p className="text-xs text-gray-400 font-mono italic">({info.row.original.partOfSpeech})</p>
            )}
          </div>
        ),
        size: 200,
      },
      {
        accessorKey: 'context',
        header: 'Context',
        cell: (info) => (
          <p className="text-sm text-trail-tan font-mono truncate max-w-xs">
            {info.getValue() as string}
          </p>
        ),
        size: 300,
      },
      {
        accessorKey: 'algorithm',
        header: 'Algorithm',
        cell: (info) => {
          const algorithm = info.getValue() as 'supermemo' | 'memrise'
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium font-mono border-2 ${
                algorithm === 'memrise'
                  ? 'bg-purple-900/30 text-purple-300 border-purple-500'
                  : 'bg-indigo-900/30 text-indigo-300 border-indigo-500'
              }`}
            >
              {algorithm === 'memrise' ? 'Memrise' : 'SuperMemo'}
            </span>
          )
        },
        size: 120,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => {
          const now = Date.now()
          const isDue = row.dueDate <= now
          const isNew = row.repetition === 0
          if (row.isMastered) return 'mastered'
          if (isNew) return 'new'
          if (isDue) return 'due'
          return 'learning'
        },
        cell: (info) => {
          const status = info.getValue() as string
          const badges = {
            mastered: 'bg-green-900/30 text-green-300 border-green-500',
            new: 'bg-blue-900/30 text-blue-300 border-blue-500',
            due: 'bg-yellow-900/30 text-yellow-300 border-yellow-500',
            learning: 'bg-gray-900/30 text-gray-300 border-gray-500',
          }
          const labels = {
            mastered: 'Mastered',
            new: 'New',
            due: 'Due Now',
            learning: 'Learning',
          }
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium font-mono border-2 ${
                badges[status as keyof typeof badges]
              }`}
            >
              {labels[status as keyof typeof labels]}
            </span>
          )
        },
        size: 120,
      },
      {
        accessorKey: 'dueDate',
        header: 'Next Review',
        cell: (info) => {
          const dueDate = info.getValue() as number
          const now = Date.now()
          const isDue = dueDate <= now
          return (
            <div>
              <p className="text-sm text-white font-mono">
                {isDue ? (
                  <span className="font-semibold text-yellow-300">Now</span>
                ) : (
                  dayjs(dueDate).fromNow()
                )}
              </p>
              <p className="text-xs text-trail-tan font-mono">
                Interval: {info.row.original.interval}{' '}
                {info.row.original.interval === 1 ? 'day' : 'days'}
              </p>
            </div>
          )
        },
        sortingFn: 'basic',
        size: 150,
      },
      {
        id: 'stats',
        header: 'Stats',
        accessorFn: (row) => row.totalReviews,
        cell: (info) => {
          const row = info.row.original
          const accuracy =
            row.totalReviews > 0
              ? Math.round((row.correctReviews / row.totalReviews) * 100)
              : 0
          return (
            <div>
              <p className="text-sm text-white font-mono">
                {row.totalReviews} {row.totalReviews === 1 ? 'review' : 'reviews'}
              </p>
              <p className="text-xs text-trail-tan font-mono">Accuracy: {accuracy}%</p>
              <p className="text-xs text-trail-tan font-mono">
                EF: {row.efactor.toFixed(2)}
                {row.algorithm === 'memrise' && row.memriseStage !== undefined && (
                  <> | Stage {row.memriseStage}</>
                )}
              </p>
            </div>
          )
        },
        size: 150,
      },
    ],
    []
  )

  const table = useReactTable({
    data: fuzzyFilteredItems,
    columns,
    state: {
      sorting,
      columnFilters,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualFiltering: true, // We're doing fuzzy filtering manually with Fuse.js
  })

  return (
    <div>
      {/* Search Bar */}
      <div className="p-4 border-b-2 border-sky-blue">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="🔍 Fuzzy search words, translations, context, definitions, notes..."
              className="w-full px-4 py-2 bg-black border-2 border-sky-blue text-white font-mono focus:border-terminal-green focus:outline-none retro-glow"
            />
          </div>
          <div className="text-sm text-trail-tan font-mono">
            {fuzzyFilteredItems.length} {fuzzyFilteredItems.length === 1 ? 'item' : 'items'}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-black border-b-2 border-sky-blue">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-3 text-left text-sm font-medium text-sky-blue font-mono"
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={
                          header.column.getCanSort()
                            ? 'cursor-pointer select-none flex items-center gap-2 hover:text-terminal-green'
                            : ''
                        }
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getIsSorted() ? (
                          header.column.getIsSorted() === 'desc' ? (
                            <span>↓</span>
                          ) : (
                            <span>↑</span>
                          )
                        ) : null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-sky-blue/30 hover:bg-gray-900/50 transition-colors"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-4">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 bg-black border-t-2 border-sky-blue flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            className="px-3 py-1 bg-black hover:bg-gray-900 text-trail-tan font-mono border-2 border-trail-tan rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            ««
          </button>
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="px-3 py-1 bg-black hover:bg-gray-900 text-trail-tan font-mono border-2 border-trail-tan rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            «
          </button>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="px-3 py-1 bg-black hover:bg-gray-900 text-trail-tan font-mono border-2 border-trail-tan rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            »
          </button>
          <button
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            className="px-3 py-1 bg-black hover:bg-gray-900 text-trail-tan font-mono border-2 border-trail-tan rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            »»
          </button>
        </div>

        <div className="flex items-center gap-2 text-sm text-trail-tan font-mono flex-wrap">
          <span>
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </span>
          <span>|</span>
          <span>
            Go to page:
            <input
              type="number"
              min="1"
              max={table.getPageCount()}
              value={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0
                table.setPageIndex(page)
              }}
              className="ml-2 w-16 px-2 py-1 bg-black border-2 border-sky-blue text-white font-mono rounded focus:border-terminal-green focus:outline-none"
            />
          </span>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value))
            }}
            className="ml-2 px-2 py-1 bg-black border-2 border-sky-blue text-white font-mono rounded focus:border-terminal-green focus:outline-none"
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}

function calculateAccuracy(items: Doc<'vocabularyItems'>[]) {
  if (items.length === 0) return 0

  const totalReviews = items.reduce((sum, item) => sum + item.totalReviews, 0)
  const correctReviews = items.reduce(
    (sum, item) => sum + item.correctReviews,
    0
  )

  if (totalReviews === 0) return 0
  return Math.round((correctReviews / totalReviews) * 100)
}

